import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter as Router } from 'react-router-dom';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createHead, UnheadProvider } from '@unhead/react/client';
import App from './App';
import { theme } from './theme';
import { getMsalConfig } from './authConfig';
import { suppressResizeObserverError } from './utils/suppressResizeObserverError';
import { restoreCart } from './utils/cartPersistence';

// Suppress ResizeObserver errors as early as possible
suppressResizeObserverError();

// Detect if we're running on localhost
const isDevelopment = window.location.hostname === 'localhost' || 
                     window.location.hostname === '127.0.0.1';

// Create QueryClient instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: isDevelopment ? false : 1,
      // Remove global staleTime to allow individual queries to set their own
      // staleTime: 5 * 60 * 1000, // Removed - let each query control its own staleTime
    },
  },
});

// Create Unhead instance for managing document head
const head = createHead();

// Helper function to wait for configuration to load
async function waitForConfig(maxAttempts = 20, delay = 100): Promise<boolean> {
  // In development with Vite, config is in import.meta.env and available immediately
  if (import.meta.env.DEV && import.meta.env.VITE_AZURE_CLIENT_ID) {
    return true;
  }

  // In production, wait for config.js to load window.ENV
  for (let i = 0; i < maxAttempts; i++) {
    // Check if configuration is available
    if ((window as any).ENV?.VITE_AZURE_CLIENT_ID) {
      return true;
    }
    // Wait before next attempt
    await new Promise(resolve => setTimeout(resolve, delay));
  }
  console.error('Configuration failed to load after', maxAttempts, 'attempts');
  return false;
}

// Initialize and render app
async function initializeApp() {
  try {
    // Restore cart early in initialization (before MSAL)
    restoreCart();
    
    // Wait for configuration to be available
    const configLoaded = await waitForConfig();
    
    if (!configLoaded) {
      // Show error message if config fails to load
      const root = ReactDOM.createRoot(
        document.getElementById('root') as HTMLElement
      );
      root.render(
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h2>Configuration Error</h2>
          <p>Failed to load application configuration. Please refresh the page or contact support.</p>
        </div>
      );
      return;
    }

    // Now create MSAL instance with loaded configuration
    const msalInstance = new PublicClientApplication(getMsalConfig());
    
    // Add event callback to handle auth errors
    msalInstance.addEventCallback((event: any) => {
      if (event.eventType === 'LOGIN_FAILURE' || event.eventType === 'ACQUIRE_TOKEN_FAILURE') {
        const error = event.error;
        
        // Check for specific authorization errors indicating AAD app needs approval
        if (error?.errorCode === 'consent_required' || 
            error?.errorCode === 'interaction_required' ||
            error?.errorCode === 'admin_consent_required' ||
            error?.errorMessage?.includes('AADSTS65001') || // Consent required
            error?.errorMessage?.includes('AADSTS50076') || // MFA required but app not approved
            error?.errorMessage?.includes('AADSTS90094') || // Admin consent required
            error?.errorMessage?.includes('AADSTS90095') || // Admin has not consented
            error?.errorMessage?.includes('AADSTS65004') || // User declined consent
            error?.errorMessage?.includes('AADSTS700003')) { // Device auth required but not approved
          // Redirect to authorization needed page
          window.location.href = '/authorization-needed';
        }
      }
    });
    
    // Initialize MSAL
    await msalInstance.initialize();
    
    // Handle redirect promise on page load
    // This will clear any pending redirect operations
    const tokenResponse = await msalInstance.handleRedirectPromise();
    if (tokenResponse) {
      // Restore cart after successful login
      restoreCart();
    }
    
    // Successfully initialized, now render the app
    const root = ReactDOM.createRoot(
      document.getElementById('root') as HTMLElement
    );

    // Add global styles to ensure proper layout
    const rootElement = document.getElementById('root') as HTMLElement;
    if (rootElement) {
      rootElement.style.height = '100%';
      rootElement.style.display = 'flex';
      rootElement.style.flexDirection = 'column';
    }

    // Also ensure html and body have proper height
    document.documentElement.style.height = '100%';
    document.body.style.height = '100%';
    document.body.style.margin = '0';
    document.body.style.display = 'flex';
    document.body.style.flexDirection = 'column';

    root.render(
      <React.StrictMode>
        <UnheadProvider head={head}>
          <QueryClientProvider client={queryClient}>
            <MsalProvider instance={msalInstance}>
              <ThemeProvider theme={theme}>
                <CssBaseline />
                <Router>
                  <App />
                </Router>
              </ThemeProvider>
            </MsalProvider>
          </QueryClientProvider>
        </UnheadProvider>
      </React.StrictMode>
    );
    
  } catch (error: any) {
    console.error('Error during MSAL initialization:', error);
    
    // Check for specific authorization errors indicating AAD app needs approval
    if (error?.errorCode === 'consent_required' || 
        error?.errorCode === 'interaction_required' ||
        error?.errorCode === 'admin_consent_required' ||
        error?.errorMessage?.includes('AADSTS65001') || // Consent required
        error?.errorMessage?.includes('AADSTS50076') || // MFA required but app not approved
        error?.errorMessage?.includes('AADSTS90094') || // Admin consent required
        error?.errorMessage?.includes('AADSTS90095')) { // Admin has not consented
      // Redirect to authorization needed page
      window.location.href = '/authorization-needed';
      return;
    }
    
    // Show generic error message for other errors
    const root = ReactDOM.createRoot(
      document.getElementById('root') as HTMLElement
    );
    root.render(
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>Authentication Error</h2>
        <p>Failed to initialize authentication. Please refresh the page or contact support.</p>
        <details style={{ marginTop: '20px' }}>
          <summary>Error Details</summary>
          <pre style={{ textAlign: 'left', background: '#f5f5f5', padding: '10px', borderRadius: '4px' }}>
            {error?.message || 'Unknown error'}
          </pre>
        </details>
      </div>
    );
  }
}

// Start the app
initializeApp();

// Report web vitals for SEO and performance monitoring
import { reportWebVitals, sendToGoogleAnalytics, checkPerformanceBudget } from './utils/webVitals';

reportWebVitals((metric) => {
  // Send to Google Analytics
  sendToGoogleAnalytics(metric);
  
  // Check performance budget
  checkPerformanceBudget(metric);
  
  // Log critical metrics in production
  if (import.meta.env.PROD && ['LCP', 'INP', 'CLS'].includes(metric.name)) {
    // Critical metrics logged to performance monitoring
  }
});
